import { Stack } from '@mui/material';
import React from 'react';

import { colors } from '../../../components';
import CustomButton from '../../../components/button/ButtonComponent';
import { CustomDrawer } from '../../../components/drawer/CustomDrawer';
import { CustomSelect } from '../../../components/select/CustomSelect';
import { TextFieldComponent } from '../../../components/text-field';
import { ACCOUNT_NUMBER_MAX_LENGTH, BANK_NAME_MAX_LENGTH } from '../../../components/validations/validations';
import { useTranslation } from '../../../utils/translations';

const addBankAccountTypeOptions = ['Checking', 'Savings'];

export interface AddBankFormData {
    accountType: string;
    nickName: string;
    bankName: string;
    routingNumber: string;
    reEnterRoutingNumber: string;
    accountNumber: string;
    reEnterAccountNumber: string;
}

interface AddBankAccountDrawerProps {
    open: boolean;
    onClose: () => void;
    country: string;
    onCountryChange: (value: string) => void;
    formData: AddBankFormData;
    formErrors: Record<string, string>;
    onFieldChange: (field: string, value: string) => void;
    onSubmit: () => void;
    showAccountNumber: boolean;
    onToggleAccountNumberVisibility: (visible: boolean) => void;
    showReEnterAccountNumber: boolean;
    onToggleReEnterAccountNumberVisibility: (visible: boolean) => void;
    showRoutingNumber: boolean;
    onToggleRoutingNumberVisibility: (visible: boolean) => void;
    showReEnterRoutingNumber: boolean;
    onToggleReEnterRoutingNumberVisibility: (visible: boolean) => void;
}

export const AddBankAccountDrawer: React.FC<AddBankAccountDrawerProps> = ({
    open,
    onClose,
    country,
    onCountryChange,
    formData,
    formErrors,
    onFieldChange,
    onSubmit,
    showAccountNumber,
    onToggleAccountNumberVisibility,
    showReEnterAccountNumber,
    onToggleReEnterAccountNumberVisibility,
    showRoutingNumber,
    onToggleRoutingNumberVisibility,
    showReEnterRoutingNumber,
    onToggleReEnterRoutingNumberVisibility,
}) => {
    const { t } = useTranslation();

    return (
        <CustomDrawer
            open={open}
            onClose={onClose}
            title={t('BankAccounts.addNewAccount')}
            width={380}
            sticky={true}
            footer={
                <Stack direction="row" gap={2} justifyContent="flex-end" pt={3}>
                    <CustomButton variant="outlined" onClick={onClose} sx={{ width: 'fit-content' }}>
                        {t('common.Cancel')}
                    </CustomButton>
                    <CustomButton variant="contained" onClick={onSubmit} sx={{ width: 'fit-content' }}>
                        {t('BankAccounts.addAccount')}
                    </CustomButton>
                </Stack>
            }
        >
            <Stack gap={2}>
                <CustomSelect
                    title={t('payments.createPayment.country')}
                    required
                    value={country}
                    onChange={value => {
                        onCountryChange(value);
                    }}
                    options={['United States', 'Canada']}
                    placeholder={t('payments.createPayment.selectYourCountry')}
                    errorValue={formErrors.country}
                    sx={{ borderRadius: 2, bgcolor: colors.fullWhite }}
                />
                <CustomSelect
                    value={formData.accountType}
                    onChange={value => onFieldChange('accountType', value)}
                    options={addBankAccountTypeOptions}
                    title={t('BankAccounts.form.accountType')}
                    required
                    placeholder="Select Account Type"
                    errorValue={formErrors.accountType}
                />
                <TextFieldComponent
                    value={formData.bankName}
                    onChange={e => onFieldChange('bankName', e.target.value)}
                    errorValue={formErrors.bankName}
                    required
                    maxLength={BANK_NAME_MAX_LENGTH}
                    label={t('BankAccounts.form.bankName')}
                />
                <TextFieldComponent
                    value={formData.nickName}
                    onChange={e => onFieldChange('nickName', e.target.value)}
                    maxLength={30}
                    label={t('payments.createPayment.nickName')}
                />
                <TextFieldComponent
                    value={formData.routingNumber}
                    onChange={e => onFieldChange('routingNumber', e.target.value)}
                    label={t('BankAccounts.form.routingNumber')}
                    errorValue={formErrors.routingNumber}
                    required
                    maxLength={9}
                    preventCopy
                    showPasswordToggle
                    onToggleVisibility={onToggleRoutingNumberVisibility}
                    type={showRoutingNumber ? 'text' : 'password'}
                />
                <TextFieldComponent
                    value={formData.reEnterRoutingNumber}
                    onChange={e => onFieldChange('reEnterRoutingNumber', e.target.value)}
                    label={t('BankAccounts.form.reEnterRoutingNumber')}
                    errorValue={formErrors.reEnterRoutingNumber}
                    required
                    maxLength={9}
                    preventCopy
                    showPasswordToggle
                    onToggleVisibility={onToggleReEnterRoutingNumberVisibility}
                    type={showReEnterRoutingNumber ? 'text' : 'password'}
                />
                <TextFieldComponent
                    value={formData.accountNumber}
                    onChange={e => onFieldChange('accountNumber', e.target.value)}
                    label={t('BankAccounts.form.accountNumber')}
                    errorValue={formErrors.accountNumber}
                    required
                    maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                    preventCopy
                    showPasswordToggle
                    onToggleVisibility={onToggleAccountNumberVisibility}
                    type={showAccountNumber ? 'text' : 'password'}
                />
                <TextFieldComponent
                    value={formData.reEnterAccountNumber}
                    onChange={e => onFieldChange('reEnterAccountNumber', e.target.value)}
                    label={t('BankAccounts.form.reEnterAccountNumber')}
                    errorValue={formErrors.reEnterAccountNumber}
                    required
                    maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                    preventCopy
                    showPasswordToggle
                    onToggleVisibility={onToggleReEnterAccountNumberVisibility}
                    type={showReEnterAccountNumber ? 'text' : 'password'}
                />
            </Stack>
        </CustomDrawer>
    );
};
